import React, { useState, useEffect } from 'react';
import { PinnedDetail, useScrollViewport } from './PinnedDetail';
import { Card, Table, RNLLoader, StatusReason } from '../UI';
import * as api from '../../api';
import { fmtNum } from '../../utils';
import PRDetailsView from './PRDetailsView';
import { SortTh, useSortState, sortRows } from './SortableHeader';

export default function DepartmentRequisitions({ openPrId, onOpenPr }) {
  const sortState = useSortState();
  const [loading, setLoading] = useState(true);
  // After `loading`, which it depends on: the scroll container does not exist
  // while the loader is showing, so the measurement has to re-run once the
  // table replaces it.
  const viewport = useScrollViewport([loading]);
  const [prs, setPrs] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  // Departments track which are OPEN, not which are closed — absent means
  // collapsed, so the page starts folded without having to wait for the data
  // to arrive before seeding a list of names to close.
  const [expandedGroups, setExpandedGroups] = useState({});
  const toggleGroup = (key) => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  // Which requisition is open. Controlled when the caller supplies openPrId,
  // uncontrolled otherwise — the aliases mean every reference below is
  // untouched either way.
  const [viewingPrIdState, setViewingPrIdState] = useState(null);
  const [viewingInstanceId, setViewingInstanceId] = useState(null);
  const viewingPrId = openPrId !== undefined ? openPrId : viewingPrIdState;
  const setViewingPrId = onOpenPr || setViewingPrIdState;

  useEffect(() => {
    const fetchPRs = async () => {
      try {
        api.clearApiCache();
        const res = await api.listAllPRs();
        setPrs(Array.isArray(res) ? res : (res?.data || []));
      } catch (e) {
        console.error("Failed to fetch PRs", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPRs();
  }, []);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return <RNLLoader title="Loading Department Requisitions..." subtitle="Fetching system data" />;

  if (viewingPrId) {
    return (
      <PRDetailsView
        prId={viewingPrId}
        instanceId={viewingInstanceId}
        onBack={() => {
          setViewingPrId(null);
          setViewingInstanceId(null);
        }}
      />
    );
  }

  const getWorkflowStepStatus = (rawStatus) => {
    const s = (rawStatus || '').toUpperCase();
    if (s === 'APPROVED') return { label: 'APPROVED', color: '#16a34a' };
    if (s === 'SUBMITTED') return { label: 'UNDER REVIEW', color: '#1d4ed8' };
    if (s === 'IN_PROGRESS') return { label: 'IN PROGRESS', color: '#7c3aed' };
    if (s === 'ON_HOLD') return { label: 'ON HOLD', color: '#b45309' };
    if (s === 'RETURNED') return { label: 'RETURNED', color: '#f97316' };
    if (s === 'REJECTED') return { label: 'REJECTED', color: '#ef4444' };
    if (s === 'DRAFT') return { label: 'DRAFT', color: '#64748b' };
    return { label: s || 'DRAFT', color: '#64748b' };
  };

  const getStatusBg = (st) => {
    const s = (st || '').toUpperCase();
    if (s === 'APPROVED') return '#dcfce7';
    if (s === 'SUBMITTED') return '#dbeafe';
    if (s === 'IN_PROGRESS') return '#ede9fe';
    if (s === 'ON_HOLD') return '#fef3c7';
    if (s === 'RETURNED') return '#ffedd5';
    if (s === 'REJECTED') return '#fee2e2';
    if (s === 'DRAFT') return '#fef9c3';
    return '#fef9c3';
  };
  const getStatusCol = (st) => {
    const s = (st || '').toUpperCase();
    if (s === 'APPROVED') return '#16a34a';
    if (s === 'SUBMITTED') return '#1d4ed8';
    if (s === 'IN_PROGRESS') return '#7c3aed';
    if (s === 'ON_HOLD') return '#b45309';
    if (s === 'RETURNED') return '#f97316';
    if (s === 'REJECTED') return '#ef4444';
    if (s === 'DRAFT') return '#ca8a04';
    return '#ca8a04';
  };

  // Only the department name banner row gets tinted — its line items stay
  // on the normal white/expanded-blue background, untouched.
  const GROUP_HEADER_BG = '#fef9c3';

  // They now sit on the page background rather than the dark header bar, so
  // they are styled as ordinary light controls.
  const headerActionStyle = {
    background: 'var(--bg-surface)',
    border: '1px solid #0b318bff',
    color: '#0b318bff',
    borderRadius: 7,
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'var(--font)',
    whiteSpace: 'nowrap',
    transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
  };

  // Fills solid on hover — spread onto both buttons so they cannot drift
  // apart. Written as handlers rather than CSS because the style is inline.
  const headerActionHover = {
    onMouseEnter: (e) => {
      e.currentTarget.style.background = '#0b318bff';
      e.currentTarget.style.color = '#ffffff';
      e.currentTarget.style.boxShadow = '0 3px 10px rgba(11,49,139,0.30)';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.background = 'var(--bg-surface)';
      e.currentTarget.style.color = '#0b318bff';
      e.currentTarget.style.boxShadow = 'none';
    },
  };

  const getPriorityBadgeStyle = (prio) => {
    const p = (prio || 'normal').toLowerCase();
    if (p === 'urgent') return { color: '#ef4444', bg: '#fee2e2', border: '#fca5a5' };
    if (p === 'high') return { color: '#d97706', bg: '#fef3c7', border: '#fcd34d' };
    return { color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' };
  };

  // Group PRs by Department
  const groupedPrs = {};
  prs.forEach(pr => {
    const deptName = pr.department_name || pr.department?.name || pr.requested_department?.name || 'Unassigned / General';
    const deptId = pr.requested_department_id || deptName;
    if (!groupedPrs[deptId]) {
      groupedPrs[deptId] = {
        name: deptName,
        items: []
      };
    }
    groupedPrs[deptId].items.push(pr);
  });

  // Every department currently on screen — what "expand all" has to open.
  const allGroupKeys = Object.values(groupedPrs).map((g) => g.name);
  const expandAllGroups = () =>
    setExpandedGroups(Object.fromEntries(allGroupKeys.map((k) => [k, true])));
  // Back to the starting state: absent means collapsed.
  const collapseAllGroups = () => setExpandedGroups({});

  const tableRows = [];

  Object.values(groupedPrs).forEach((group) => {
    // Add group header
    const groupTotal = group.items.reduce((acc, curr) => acc + parseFloat(curr.total_estimated_cost || 0), 0);
    const groupKey = group.name;
    const isCollapsed = !expandedGroups[groupKey];
    tableRows.push({
      _isGroupHeader: true,
      id: `group-${group.name}`,
      _groupKey: groupKey,
      content: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '4px 12px' }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#0b318bff', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, width: 12, display: 'inline-block' }}>{isCollapsed ? '▶' : '▼'}</span>
            {group.name}
            {/* The count stays visible while collapsed, so a folded-away
                department still says how much is in it. */}
            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'none' }}>
              ({group.items.length} {group.items.length === 1 ? 'requisition' : 'requisitions'})
            </span>
          </h4>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#0b318bff' }}>Total: ₦{fmtNum(groupTotal)}</div>
        </div>
      )
    });

    if (isCollapsed) return;

    // Sorted inside the group, never across it: this list exists to show one
    // department at a time, and a global sort would interleave them.
    sortRows(group.items, sortState, (pr, key) => {
      switch (key) {
        case '_date': return pr.required_date || pr.created_at?.split('T')[0] || '';
        case '_requester':
          return pr.requested_by_name
            || (pr.requested_by ? `${pr.requested_by.first_name || ''} ${pr.requested_by.last_name || ''}`.trim() : '')
            || '';
        case '_type': return pr.request_type_name || '';
        case '_project': {
          const many = pr.items?.length > 1
            && new Set(pr.items.map((i) => i.project_id)).size > 1;
          return many ? 'Multiple Projects'
            : (pr.project_name || pr.items?.[0]?.project_name || '');
        }
        case '_priority': return pr.priority_status || pr.items?.[0]?.priority_status || '';
        case '_lines': return pr.items?.length || 0;
        case 'total_estimated_cost': return Number(pr.total_estimated_cost) || 0;
        default: return pr[key];
      }
    }).forEach(pr => {
      const prProjectName = pr.project_name || pr.items?.[0]?.project_name || '—';
      const isMultipleProjects = pr.items?.length > 1 && new Set(pr.items.map(i => i.project_id)).size > 1;

      tableRows.push({
        _isGroupHeader: false,
        id: pr.id || pr.purchase_request_id,
        pr_number: pr.pr_number || 'DRAFT',
        date: pr.required_date || pr.created_at?.split('T')[0] || '—',
        requester: pr.requested_by_name || pr.requested_by?.full_name || (pr.requested_by ? `${pr.requested_by.first_name || ''} ${pr.requested_by.last_name || ''}`.trim() : '') || '—',
        type: pr.request_type_name || pr.purchase_request_type?.name || pr.type?.name || '—',
        project: isMultipleProjects ? 'Multiple Projects' : prProjectName,
        priority: pr.priority_status || pr.items?.[0]?.priority_status || 'Normal',
        lines: pr.items?.length || 0,
        total_estimated_cost: pr.total_estimated_cost || 0,
        approved_amount: pr.request_status === 'APPROVED' ? pr.total_estimated_cost : 0,
        paid_amount: 0,
        currently_with: pr.currently_with || '—',
        request_status: pr.request_status || 'DRAFT',
        _raw: pr
      });

      if (expandedRows[pr.id || pr.purchase_request_id] && pr.items && pr.items.length > 0) {
        tableRows.push({
          _isExpandedContent: true,
          parentId: pr.id || pr.purchase_request_id,
          content: (
            <div style={{ padding: '16px 48px', background: '#f1f5f9', borderTop: '2px solid #3b82f6', borderBottom: '2px solid #cbd5e1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                  PURPOSE: {pr.title}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewingPrId(pr.id || pr.purchase_request_id);
                    setViewingInstanceId(pr.workflow_instance_id || null);
                  }}
                  style={{
                    background: '#0b318bff', color: '#fff', border: 'none',
                    padding: '6px 16px', borderRadius: 4, cursor: 'pointer',
                    fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  View
                </button>
              </div>
              <div className="inner-items-table" style={{ border: '1px solid black', borderRadius: 6, overflow: 'hidden' }}>
                <Table 
                  cols={[
                    { key: '_sn', label: '#', render: (_, __, i) => i + 1 },
                    { key: 'description', label: 'ITEM', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
                    { key: 'unit_of_measure', label: 'UNIT' },
                    { key: 'quantity', label: 'REQ QTY', align: 'center', render: v => <span style={{ fontWeight: 600 }}>{fmtNum(v)}</span> },
                    { key: 'appr_qty', label: 'APPR QTY', align: 'center', render: (_, row) => <span style={{ fontWeight: 600, color: '#16a34a' }}>{fmtNum(row.quantity)}</span> },
                    { key: 'paid_qty', label: 'PAID QTY', align: 'center', render: () => <span style={{ color: '#3b82f6' }}>—</span> },
                    { key: 'balance', label: 'BALANCE', align: 'center', render: (_, row) => <span style={{ fontWeight: 600, color: '#ea580c' }}>{fmtNum(row.quantity)}</span> },
                    { key: 'estimated_unit_rate', label: 'RATE', align: 'right', render: v => `₦${fmtNum(v)}` },
                    { key: 'estimated_amount', label: 'EST COST', align: 'right', render: v => <span style={{ fontWeight: 700 }}>₦{fmtNum(v)}</span> },
                    { key: 'cc_status', label: 'CC STATUS', render: () => {
                       const s = getWorkflowStepStatus(pr.request_status);
                       return <span style={{ color: s.color, fontWeight: 600, fontSize: 11 }}>{s.label}</span>;
                    }},
                    { key: 'appr_status', label: 'APPR STATUS', render: () => {
                       const s = getWorkflowStepStatus(pr.request_status);
                       return <span style={{ color: s.color, fontWeight: 600, fontSize: 11 }}>{s.label}</span>;
                    }},
                    { key: 'payments', label: 'PAYMENTS', render: () => <span style={{ color: 'var(--text-muted)' }}>No payments</span> }
                  ]}
                  rows={pr.items}
                />
              </div>
            </div>
          )
        });
      }
    });
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Caption and controls share one line — stacked, they were two flex
          children with a gap each, which left an obvious band of empty space
          above the grid. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
          {/* The API scopes this to the viewer: MD/DMD/super_admin get every
              department, everyone else gets the department(s) they belong to.
              The old copy claimed "all system requisitions" to everyone. */}
          Requisitions grouped by department.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={expandAllGroups} title="Open every department" style={headerActionStyle} {...headerActionHover}>
            Expand all
          </button>
          <button type="button" onClick={collapseAllGroups} title="Close every department" style={headerActionStyle} {...headerActionHover}>
            Collapse all
          </button>
        </div>
      </div>

      <style>{`
        .inner-items-table {
          border: 1px solid black !important;
        }
        .inner-items-table table, .inner-items-table th, .inner-items-table td {
          border: 1px solid black !important;
        }
      `}</style>

      <Card style={{ border: 'none', boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        <div ref={viewport.ref} style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, border: '2px solid black' }}>
            <thead>
              <tr style={{ background: '#0b318bff', color: '#fff' }}>
                <SortTh label="REF" sortKey="pr_number" state={sortState} style={{ textAlign: 'left', border: '1px solid black' }} />
                <SortTh label="DATE" sortKey="_date" state={sortState} style={{ textAlign: 'left', border: '1px solid black' }} />
                <SortTh label="REQUESTER" sortKey="_requester" state={sortState} style={{ textAlign: 'left', border: '1px solid black' }} />
                <SortTh label="TYPE" sortKey="_type" state={sortState} style={{ textAlign: 'left', border: '1px solid black' }} />
                <SortTh label="PROJECT" sortKey="_project" state={sortState} style={{ textAlign: 'left', border: '1px solid black' }} />
                <SortTh label="PRIORITY" sortKey="_priority" state={sortState} style={{ textAlign: 'left', border: '1px solid black' }} />
                <SortTh label="LINES" sortKey="_lines" state={sortState} style={{ textAlign: 'center', border: '1px solid black' }} />
                <SortTh label="EST. AMOUNT" sortKey="total_estimated_cost" state={sortState} style={{ textAlign: 'right', border: '1px solid black' }} />
                <SortTh label="APPROVED" style={{ textAlign: 'right', border: '1px solid black' }} />
                {/* PAID hidden on request — nothing disburses against a
                    requisition yet, so it reads ₦0.00 on every row. Uncomment
                    this and its <td> below to restore it. */}
                {/* <SortTh label="PAID" style={{ textAlign: 'right', border: '1px solid black' }} /> */}
                <SortTh label="CURRENTLY WITH" sortKey="currently_with" state={sortState} style={{ textAlign: 'center', border: '1px solid black' }} />
                <SortTh label="STATUS" sortKey="request_status" state={sortState} style={{ textAlign: 'center', border: '1px solid black' }} />
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, idx) => {
                if (row._isGroupHeader) {
                  return (
                    <tr
                      key={row.id}
                      onClick={() => toggleGroup(row._groupKey)}
                      title={expandedGroups[row._groupKey] ? 'Hide this department' : 'Show this department'}
                      style={{ cursor: 'pointer' }}
                    >
                      <td colSpan={11} style={{ padding: '12px', background: GROUP_HEADER_BG, border: '2px solid black', borderTop: idx > 0 ? '3px solid black' : '2px solid black' }}>
                        {row.content}
                      </td>
                    </tr>
                  );
                }
                if (row._isExpandedContent) {
                  return (
                    <tr key={`expand-${row.parentId}`}>
                      <td colSpan={11} style={{ padding: 0, border: '1px solid black' }}>
                        <PinnedDetail width={viewport.width}>
                          {row.content}
                        </PinnedDetail>
                      </td>
                    </tr>
                  );
                }

                const s = getWorkflowStepStatus(row.request_status);

                const tdB = { padding: '12px', border: '1px solid black' };

                return (
                  <tr
                    key={row.id}
                    onClick={() => toggleRow(row.id)}
                    style={{
                      background: expandedRows[row.id] ? '#dbeafe' : '#fff',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease'
                    }}
                  >
                    <td style={{ ...tdB, fontWeight: 700, color: '#0b318bff', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 10, color: '#64748b', lineHeight: 1 }}>
                          {expandedRows[row.id] ? '▼' : '▶'}
                        </span>
                        <span>{row.pr_number}</span>
                      </div>
                    </td>
                    <td style={tdB}>{row.date}</td>
                    <td style={tdB}>{row.requester}</td>
                    <td style={tdB}>{row.type}</td>
                    <td style={tdB}>{row.project}</td>
                    <td style={{ ...tdB, textAlign: 'center' }}>
                       {(() => {
                         const pStyle = getPriorityBadgeStyle(row.priority);
                         return (
                           <span style={{
                             color: pStyle.color,
                             background: pStyle.bg,
                             border: `1px solid ${pStyle.border}`,
                             padding: '4px 10px',
                             borderRadius: 12,
                             fontSize: 11,
                             fontWeight: 700,
                             display: 'inline-block',
                             textTransform: 'capitalize',
                             whiteSpace: 'nowrap'
                           }}>
                             {row.priority}
                           </span>
                         );
                       })()}
                    </td>
                    <td style={{ ...tdB, textAlign: 'center', fontWeight: 600 }}>{row.lines}</td>
                    <td style={{ ...tdB, textAlign: 'right', fontWeight: 700 }}>₦{fmtNum(row.total_estimated_cost)}</td>
                    <td style={{ ...tdB, textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>₦{fmtNum(row.approved_amount)}</td>
                    {/* PAID — hidden, see the header above. */}
                    {/* <td style={{ ...tdB, textAlign: 'right', fontWeight: 700, color: '#3b82f6' }}>₦{fmtNum(row.paid_amount)}</td> */}
                    <td style={{ ...tdB, textAlign: 'center', fontWeight: 600 }}>{row.currently_with}</td>
                    <td style={{ ...tdB, textAlign: 'center' }}>
                      <span style={{
                        background: getStatusBg(row.request_status), color: getStatusCol(row.request_status),
                        border: `1px solid ${getStatusCol(row.request_status)}40`,
                        padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                        display: 'inline-block', whiteSpace: 'nowrap'
                      }}>
                        {s.label}
                      </span>
                      <StatusReason reason={row.status_reason} />
                    </td>
                  </tr>
                );
              })}
              {tableRows.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No requisitions found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
